import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useApolloClient } from "@apollo/client"

import { NOT_FOUND } from "shared/constants/permalinks"
import { useNavigationContext } from "shared/context/navigation/NavigationContext"
import { SHARED_PROJECT_ACTIONS_OVERLAY } from "shared/context/overlay/types"
import { findChildren, reduceTree } from "shared/lib/treeUtils"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { useTitle } from "shared/lib/usePageTitle"
import {
  ProjectChild,
  RoomTypename,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"

import { readProjectChildFragment } from "entities/project-children/lib/readProjectChildFragment"
import { useProjectChildrenCache } from "entities/projects/model/useProjectChildrenCache"
import { useNavigateToPermalink } from "features/navigation/model/useNavigateToPermalink"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { useSharedProject } from "features/project-sharing/model/useSharedProject"
import { ProjectPageModel } from "pages/project-page/ProjectPage"
import { useProjectPageAdditionalActions } from "pages/project-page/hooks/useProjectPageAdditionalActions"
import { useProjectTabState } from "pages/project-page/hooks/useProjectTabState"
import { ProjectChatButton } from "widgets/project-chat-button/ProjectChatButton"

import { SharedProjectPageConnectorProps } from "../SharedProjectPageConnector"
import { SharedProjectPageSidebar } from "../sidebar/SharedProjectPageSidebar"

export function useSharedProjectPageModel({
  projectShareID,
  page
}: SharedProjectPageConnectorProps): ProjectPageModel {
  const { t } = useTranslation()
  const projectModel = useSharedProject(
    { projectShareID },
    { includePrice: false, includeAttachments: true }
  )
  const project = projectModel.core.data.project?.data
  const isLoading = projectModel.core.data.project?.isLoading
  const { openOverlay: openSharedProjectActionsOverlay } = useOverlay(
    SHARED_PROJECT_ACTIONS_OVERLAY
  )

  const { isAuthenticated } = useAuth()

  useProjectChildrenCache(project?.children)

  const { navigate } = useNavigateToPermalink()

  useEffect(() => {
    if (!project && !isLoading) {
      navigate(NOT_FOUND)
    }
  }, [project, isLoading])

  const { notify } = useNavigationContext()
  useEffect(() => {
    notify()
  }, [projectShareID])

  const {
    activeTabID: requestedTabID,
    isDetailsViewOpen,
    selectedElementIDs,
    collapsedRoomIDs,
    expandedProductPositionIDs,
    activateFloor,
    collapseRoom,
    expandProductPosition,
    collapseAll,
    expandAll,
    selectElement,
    unselectAll,
    openElementDetails
  } = useProjectStore(projectShareID)

  const { cache } = useApolloClient()

  const isItemCollapsed = useCallback(
    (item: { id: string; __typename?: string }) => {
      if (item.__typename === RoomTypename) {
        return collapsedRoomIDs.includes(item.id)
      }
      return !expandedProductPositionIDs.includes(item.id)
    },
    [collapsedRoomIDs, expandedProductPositionIDs]
  )

  const onItemCollapse = useEvent((id: string) => {
    const child = readProjectChildFragment(id, cache)
    if (isProjectRoom(child)) collapseRoom(id)
    else expandProductPosition(id)
  })

  const onCollapseAll = useEvent(() => {
    const children = project?.children ?? []
    const roomIDs = reduceTree(
      children,
      (acc: string[], item) => {
        if (isProjectRoom(item)) acc.push(item.id)
        return acc
      },
      []
    )
    collapseAll(roomIDs)
  })

  const onExpandAll = useEvent(() => {
    const children = project?.children ?? []
    const ppIDs = reduceTree(
      children,
      (acc: string[], item) => {
        if (isProjectProductPosition(item)) acc.push(item.id)
        return acc
      },
      []
    )
    expandAll(ppIDs)
  })

  const { activeTabID } = useProjectTabState({
    projectModel,
    requestedTabID
  })

  useEffect(() => {
    if (requestedTabID !== activeTabID) {
      activateFloor(activeTabID)
    }
  }, [requestedTabID, activeTabID])

  useTitle(project?.title)

  const selectedChildren = useMemo(
    () => findChildren<ProjectChild>(selectedElementIDs, project?.children),
    [selectedElementIDs]
  )

  const openChildren = useMemo(
    () => (isDetailsViewOpen ? selectedChildren : []),
    [selectedElementIDs, isDetailsViewOpen]
  )

  const additionalActions = useProjectPageAdditionalActions({
    projectID: projectShareID,
    page
  })

  const handleShowDetails = useEvent(() => {
    unselectAll()
    openElementDetails()
  })

  const handleOpenActionsOverlay = useEvent(() => {
    if (project) {
      openSharedProjectActionsOverlay({
        projectID: project.id,
        projectShareID,
        additionalActions
      })
    }
  })

  const actionsButtonLabel = useMemo(() => {
    if (isProjectRoom(selectedChildren[0])) {
      return t("Room.Actions.Actions")
    }
    if (isProjectProductPosition(selectedChildren[0])) {
      return t("ProductPosition.Actions.Actions")
    }
    return t("Project.Actions.Actions")
  }, [selectedChildren, t])

  return {
    projectModel: {
      ...projectModel,
      core: {
        ...projectModel.core,
        handlers: {
          ...projectModel.core.handlers,
          onShowDetails: handleShowDetails
        }
      }
    },
    selectedElementIDs,
    isItemCollapsed,
    onItemCollapse,
    onCollapseAll,
    onExpandAll,
    onSelect: selectElement,
    onActions: handleOpenActionsOverlay,
    actionsButtonLabel,
    activeTabID,
    onTabSelect: activateFloor,

    hideChat: isAuthenticated,

    chatWidgets: null, // <ContentTag content={page} attribute="chatWidgets" />,
    chatButton: <ProjectChatButton page={page} />,

    sidebar: (
      <SharedProjectPageSidebar
        projectID={projectShareID}
        selectedChildren={openChildren}
      />
    )
  }
}
